// The restaurant's coupons: the list, one form for create and edit (the
// API takes the whole document both ways), delete (refused once redeemed —
// deactivate instead) and each coupon's redemption ledger. Codes are
// upper-cased by the server.
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Plus, Trash2 } from 'lucide-react';
import { ApiError, api, dateTimeOf, money } from '../../lib/api';
import { useScope } from '../../scope';
import {
  fromBp,
  messageOf,
  toBp,
  toCents,
  toDollars,
  toIntOrNull,
  type Coupon,
  type CouponInput,
  type CouponRedemption,
  type CouponType,
} from './types';

type CouponForm = {
  couponId: string | null;
  code: string;
  couponType: CouponType;
  amount: string; // dollars, flat_amount only
  percent: string; // percent, percent only
  startsOn: string;
  endsOn: string;
  appliesPickup: boolean;
  appliesDelivery: boolean;
  appliesDineIn: boolean;
  minOrder: string; // dollars
  maxRedemptions: string;
  perCustomerLimit: string;
  firstOrderOnly: boolean;
  exclusive: boolean;
  active: boolean;
  // carried through an edit untouched (store targeting and weekdays have no editor yet)
  allStores: boolean;
  storeIds: string[];
  daysOfWeek: number[];
};

const BLANK: CouponForm = {
  couponId: null, code: '', couponType: 'flat_amount', amount: '', percent: '',
  startsOn: '', endsOn: '', appliesPickup: true, appliesDelivery: true, appliesDineIn: true,
  minOrder: '', maxRedemptions: '', perCustomerLimit: '',
  firstOrderOnly: false, exclusive: false, active: true, allStores: true, storeIds: [], daysOfWeek: [],
};

function formOf(c: Coupon): CouponForm {
  return {
    couponId: c.couponId,
    code: c.code,
    couponType: c.couponType,
    amount: toDollars(c.amountInCents),
    percent: fromBp(c.percentBp),
    startsOn: c.startsOn ?? '',
    endsOn: c.endsOn ?? '',
    appliesPickup: c.appliesPickup,
    appliesDelivery: c.appliesDelivery,
    appliesDineIn: c.appliesDineIn,
    minOrder: toDollars(c.minOrderInCents),
    maxRedemptions: c.maxRedemptions === null ? '' : String(c.maxRedemptions),
    perCustomerLimit: c.perCustomerLimit === null ? '' : String(c.perCustomerLimit),
    firstOrderOnly: c.firstOrderOnly,
    exclusive: c.exclusive,
    active: c.active,
    allStores: c.allStores,
    storeIds: c.storeIds,
    daysOfWeek: c.daysOfWeek,
  };
}

function bodyOf(f: CouponForm): CouponInput {
  return {
    code: f.code.trim(),
    couponType: f.couponType,
    amountInCents: f.couponType === 'flat_amount' ? toCents(f.amount) : null,
    percentBp: f.couponType === 'percent' ? toBp(f.percent) : null,
    startsOn: f.startsOn || null,
    endsOn: f.endsOn || null,
    appliesPickup: f.appliesPickup,
    appliesDelivery: f.appliesDelivery,
    appliesDineIn: f.appliesDineIn,
    minOrderInCents: f.minOrder.trim() ? toCents(f.minOrder) : null,
    maxRedemptions: f.maxRedemptions.trim() ? toIntOrNull(f.maxRedemptions) : null,
    perCustomerLimit: f.perCustomerLimit.trim() ? toIntOrNull(f.perCustomerLimit) : null,
    firstOrderOnly: f.firstOrderOnly,
    exclusive: f.exclusive,
    active: f.active,
    allStores: f.allStores,
    storeIds: f.storeIds,
    daysOfWeek: f.daysOfWeek,
  };
}

const valueOf = (c: Coupon): string =>
  c.couponType === 'flat_amount' ? `${money(c.amountInCents ?? 0)} off`
    : c.couponType === 'percent' ? `${(c.percentBp ?? 0) / 100}% off`
    : 'Free delivery';

const channelsOf = (c: Coupon): string =>
  [c.appliesPickup ? 'Pickup' : null, c.appliesDelivery ? 'Delivery' : null, c.appliesDineIn ? 'Dine-in' : null]
    .filter(Boolean)
    .join(' · ') || 'No channels';

const rulesOf = (c: Coupon): string =>
  [
    c.minOrderInCents !== null ? `min ${money(c.minOrderInCents)}` : null,
    c.maxRedemptions !== null ? `max ${c.maxRedemptions}` : null,
    c.perCustomerLimit !== null ? `${c.perCustomerLimit}/customer` : null,
    c.firstOrderOnly ? 'first order only' : null,
    c.exclusive ? 'exclusive' : null,
    c.allStores ? null : `${c.storeIds.length} store${c.storeIds.length === 1 ? '' : 's'}`,
    c.daysOfWeek.length > 0 ? `days ${c.daysOfWeek.join(',')}` : null,
  ].filter(Boolean).join(' · ') || '—';

function Check(props: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
      <input type="checkbox" checked={props.checked} onChange={(e) => props.onChange(e.target.checked)} />
      {props.label}
    </label>
  );
}

export function Coupons() {
  const { rid, sid, store } = useScope();
  const R = `/restaurants/${rid}`;
  const [coupons, setCoupons] = useState<Coupon[] | null>(null);
  const [form, setForm] = useState<CouponForm | null>(null);
  const [redemptionsFor, setRedemptionsFor] = useState<string | null>(null);
  const [redemptions, setRedemptions] = useState<CouponRedemption[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      setCoupons(await api.get<Coupon[]>(`${R}/coupons`));
      setError(null);
    } catch (e) {
      setError(messageOf(e, 'Failed to load.'));
    }
  }, [R]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (!form) return;
    setBusy(true);
    try {
      const body = bodyOf(form);
      if (form.couponId) await api.put<Coupon>(`${R}/coupons/${form.couponId}`, body);
      else await api.post<Coupon>(`${R}/coupons`, body);
      setForm(null);
      await load();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(messageOf(e, 'Save failed.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: Coupon) {
    if (!confirm(`Delete coupon ${c.code}?`)) return;
    setBusy(true);
    try {
      await api.del(`${R}/coupons/${c.couponId}`);
      if (redemptionsFor === c.couponId) setRedemptionsFor(null);
      await load();
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'coupon_redeemed'
          ? `${c.code} has been redeemed, so its ledger keeps it — edit it and untick Active instead.`
          : messageOf(e, 'Delete failed.'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function toggleRedemptions(c: Coupon) {
    if (redemptionsFor === c.couponId) {
      setRedemptionsFor(null);
      return;
    }
    setRedemptionsFor(c.couponId);
    setRedemptions(null);
    try {
      setRedemptions(await api.get<CouponRedemption[]>(`${R}/coupons/${c.couponId}/redemptions`));
    } catch (e) {
      setError(messageOf(e, 'Failed to load redemptions.'));
    }
  }

  const set = (patch: Partial<CouponForm>) => form && setForm({ ...form, ...patch });

  return (
    <>
      <div className="page-head" style={{ alignItems: 'center' }}>
        <div>
          <div className="overline">Customers · Coupons</div>
          <h1>Coupons</h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          {saved && <span style={{ color: 'var(--ok)', fontSize: 13.5, fontWeight: 700 }}>Saved ✓</span>}
          <button className="btn btn-primary" onClick={() => setForm(form ? null : { ...BLANK })}>
            <Plus size={16} /> New coupon
          </button>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      {form && (
        <div className="panel" data-section="coupon-form" style={{ marginBottom: 20 }}>
          <h2>{form.couponId ? `Edit ${form.code}` : 'New coupon'}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, paddingTop: 8 }}>
            <div className="field">
              <label>Code</label>
              <input className="input" aria-label="Coupon code" placeholder="TEN" value={form.code}
                onChange={(e) => set({ code: e.target.value })} />
            </div>
            <div className="field">
              <label>Type</label>
              <select className="input" aria-label="Coupon type" value={form.couponType}
                onChange={(e) => set({ couponType: e.target.value as CouponType })}>
                <option value="flat_amount">Flat amount</option>
                <option value="percent">Percent</option>
                <option value="free_delivery">Free delivery</option>
              </select>
            </div>
            {form.couponType === 'flat_amount' && (
              <div className="field">
                <label>Amount off ($)</label>
                <input className="input money" title="Amount off" inputMode="decimal" placeholder="10.00" value={form.amount}
                  onChange={(e) => set({ amount: e.target.value })} />
              </div>
            )}
            {form.couponType === 'percent' && (
              <div className="field">
                <label>Percent off (%)</label>
                <input className="input money" aria-label="Percent off" inputMode="decimal" placeholder="10" value={form.percent}
                  onChange={(e) => set({ percent: e.target.value })} />
              </div>
            )}
            <div className="field">
              <label>Starts on</label>
              <input className="input" aria-label="Starts on" type="date" value={form.startsOn}
                onChange={(e) => set({ startsOn: e.target.value })} />
            </div>
            <div className="field">
              <label>Ends on</label>
              <input className="input" aria-label="Ends on" type="date" value={form.endsOn}
                onChange={(e) => set({ endsOn: e.target.value })} />
            </div>
            <div className="field">
              <label>Minimum order ($)</label>
              <input className="input money" title="Minimum order" inputMode="decimal" placeholder="No minimum" value={form.minOrder}
                onChange={(e) => set({ minOrder: e.target.value })} />
            </div>
            <div className="field">
              <label>Max redemptions</label>
              <input className="input" aria-label="Max redemptions" inputMode="numeric" placeholder="Unlimited" value={form.maxRedemptions}
                onChange={(e) => set({ maxRedemptions: e.target.value })} />
            </div>
            <div className="field">
              <label>Per-customer limit</label>
              <input className="input" aria-label="Per-customer limit" inputMode="numeric" placeholder="Unlimited" value={form.perCustomerLimit}
                onChange={(e) => set({ perCustomerLimit: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, marginTop: 14, flexWrap: 'wrap' }}>
            <Check label="Pickup" checked={form.appliesPickup} onChange={(v) => set({ appliesPickup: v })} />
            <Check label="Delivery" checked={form.appliesDelivery} onChange={(v) => set({ appliesDelivery: v })} />
            <Check label="Dine-in" checked={form.appliesDineIn} onChange={(v) => set({ appliesDineIn: v })} />
            <span style={{ borderLeft: '1px solid var(--line)' }} />
            <Check label="First order only" checked={form.firstOrderOnly} onChange={(v) => set({ firstOrderOnly: v })} />
            <Check label="Exclusive" checked={form.exclusive} onChange={(v) => set({ exclusive: v })} />
            <Check label="Active" checked={form.active} onChange={(v) => set({ active: v })} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : form.couponId ? 'Save coupon' : 'Create coupon'}
            </button>
            <button className="btn btn-outline" disabled={busy} onClick={() => setForm(null)}>
              Discard
            </button>
          </div>
        </div>
      )}

      {coupons === null && <div className="loading">Loading…</div>}
      {coupons?.length === 0 && !form && <div className="loading">No coupons yet — create the first one.</div>}
      {coupons && coupons.length > 0 && (
        <table className="tbl" data-section="coupons">
          <thead>
            <tr>
              <th>Code</th>
              <th>Value</th>
              <th>Channels</th>
              <th>Window</th>
              <th>Rules</th>
              <th className="num">Redeemed</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {coupons.map((c) => (
              <Fragment key={c.couponId}>
                <tr data-code={c.code} onClick={() => void toggleRedemptions(c)}>
                  <td>
                    <span className="docket">{c.code}</span>
                    {!c.active && <span className="chip chip-cancelled" style={{ marginLeft: 10 }}>inactive</span>}
                  </td>
                  <td style={{ fontWeight: 700 }}>{valueOf(c)}</td>
                  <td style={{ color: 'var(--ink-2)' }}>{channelsOf(c)}</td>
                  <td style={{ color: 'var(--ink-2)' }}>
                    {c.startsOn || c.endsOn ? `${c.startsOn ?? '…'} → ${c.endsOn ?? '…'}` : 'Always'}
                  </td>
                  <td style={{ color: 'var(--ink-3)', fontSize: 13 }} data-field="rules">{rulesOf(c)}</td>
                  <td className="num money" style={{ fontWeight: 700 }} data-field="redemptions">{c.redemptionCount}</td>
                  <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <button className="btn btn-outline btn-sm" disabled={busy}
                        aria-label={`Redemptions of ${c.code}`} onClick={() => void toggleRedemptions(c)}>
                        Ledger
                      </button>
                      <button className="btn btn-outline btn-sm" disabled={busy}
                        aria-label={`Edit coupon ${c.code}`} onClick={() => setForm(formOf(c))}>
                        Edit
                      </button>
                      <button className="btn btn-danger btn-sm" disabled={busy} title="Delete coupon"
                        aria-label={`Delete coupon ${c.code}`} onClick={() => void remove(c)}>
                        <Trash2 size={14} />
                      </button>
                    </span>
                  </td>
                </tr>
                {redemptionsFor === c.couponId && (
                  <tr style={{ cursor: 'default' }}>
                    <td colSpan={7} style={{ background: 'var(--bg-sunken)' }} data-section="redemptions">
                      {redemptions === null && <div className="loading">Loading redemptions…</div>}
                      {redemptions?.length === 0 && <div className="loading">Never redeemed.</div>}
                      {redemptions?.map((r) => (
                        <div key={r.redemptionId} className="rowline" data-order-id={r.orderId}>
                          <span className="tm">{dateTimeOf(r.redeemedAt, store?.timezone)}</span>
                          <div className="grow">
                            {r.storeId === sid ? (
                              <Link className="money" style={{ fontWeight: 700 }} to={`/r/${rid}/s/${sid}/orders/${r.orderId}`}>
                                order {r.orderId}
                              </Link>
                            ) : (
                              <b className="money">order {r.orderId}</b>
                            )}
                            <span className="sub">
                              {r.customerId ? `customer ${r.customerId}` : 'guest'}
                              {r.storeId === sid ? ' · this store' : ` · store ${r.storeId}`}
                            </span>
                          </div>
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
